import {
  ActiveRoleType,
  AgeGroupType,
  UserProfileType,
  UserInfoType,
  UserPermissionType,
  UserRoleType,
  UserWorkspaceCapabilities,
  WorkspaceType,
} from "@/interFace/general";
import { PlanTierEnum, SchoolRoleEnum } from "@/interFace/courses/types";

/*
 * TODO:
 * Natural next steps
 * Replace any remaining direct roleId-based UI checks elsewhere with permissions
 * Extend the backend payload to return roles directly instead of only roleId
 * Add activeRole switching in profile/settings for multi-role users
 */

const EMPTY_CAPABILITIES: UserWorkspaceCapabilities = {
  canAccessStudentWorkspace: false,
  canAccessTutorWorkspace: false,
  canAccessParentWorkspace: false,
  canAccessAdminWorkspace: false,
  canLearn: false,
  canPlay: false,
  canReview: false,
  canCreateQuiz: false,
  canManageClassrooms: false,
  canManageChildren: false,
  canViewQuizPlans: false,
  canManageSchoolQuizzes: false,
};

export const EMPTY_USER_INFO: UserInfoType = {
  roles: [],
  profiles: [],
  isTester: false,
  activeRole: null,
  workspaces: [],
  activeWorkspace: null,
  defaultWorkspace: null,
  capabilities: EMPTY_CAPABILITIES,
  permissions: [],
  roleId: 0,
  accountTypeId: 0,
  age: null,
  ageGroup: null,
};

export const getAgeGroup = (age: number | null): AgeGroupType | null => {
  if (typeof age !== "number") {
    return null;
  }

  return age >= 16 ? "16plus" : "under16";
};

export const MIN_RESTRICTED_CHILD_CONTACT_AGE = 4;
export const MAX_RESTRICTED_CHILD_CONTACT_AGE = 12;

export const getRolesFromRoleId = (
  roleId: number,
  accountTypeId: number
): UserRoleType[] => {

  switch (roleId) {
    case SchoolRoleEnum.Student:
      return ["student"];
    case SchoolRoleEnum.Parent:
      return ["parent"];
    case SchoolRoleEnum.Teacher:
      return ["teacher"];
    case SchoolRoleEnum.Tutor:
      return ["tutor"];
    case SchoolRoleEnum.School:
    case SchoolRoleEnum.System:
      return ["admin"];
    case SchoolRoleEnum.Claimant:
      return ["claimant"];
    default:
      return [];
  }
};

export const isRestrictedChildContactAge = (age: number | null): boolean => {
  if (typeof age !== "number") {
    return false;
  }

  return age >= MIN_RESTRICTED_CHILD_CONTACT_AGE && age <= MAX_RESTRICTED_CHILD_CONTACT_AGE;
};

export const isRestrictedChildContactUser = (userInfo: UserInfoType): boolean => {
  return userInfo.roles.includes("student") && isRestrictedChildContactAge(userInfo.age);
};

const normalizeRoles = (roles: UserRoleType[] | undefined): UserRoleType[] => {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles.filter((role, index, currentRoles) => {
    return currentRoles.indexOf(role) === index;
  });
};

const normalizeProfiles = (profiles: UserProfileType[] | undefined): UserProfileType[] => {
  if (!Array.isArray(profiles)) {
    return [];
  }

  return profiles
    .filter((profile): profile is UserProfileType => typeof profile === "string" && profile.trim().length > 0)
    .map((profile) => profile.trim().toLowerCase())
    .filter((profile, index, currentProfiles) => currentProfiles.indexOf(profile) === index);
};

const normalizeWorkspaces = (workspaces: WorkspaceType[] | undefined): WorkspaceType[] => {
  if (!Array.isArray(workspaces)) {
    return [];
  }

  return workspaces.filter((workspace, index, currentWorkspaces) => {
    return currentWorkspaces.indexOf(workspace) === index;
  });
};

const getActiveRole = (
  roles: UserRoleType[],
  preferredActiveRole?: ActiveRoleType
): ActiveRoleType => {
  if (preferredActiveRole && roles.includes(preferredActiveRole)) {
    return preferredActiveRole;
  }

  return roles[0] ?? null;
};

const getWorkspacesFromRoles = (roles: UserRoleType[]): WorkspaceType[] => {
  const workspaces = new Set<WorkspaceType>();

  if (roles.includes("student")) {
    workspaces.add("student");
  }

  if (roles.includes("teacher") || roles.includes("tutor") || roles.includes("admin")) {
    workspaces.add("tutor");
  }

  if (roles.includes("claimant")) {
    workspaces.add("claimant");
  }

  if (roles.includes("parent")) {
    workspaces.add("parent");
  }

  return Array.from(workspaces);
};

const getDefaultWorkspace = (
  workspaces: WorkspaceType[],
  preferredWorkspace?: WorkspaceType | null
): WorkspaceType | null => {
  if (preferredWorkspace && workspaces.includes(preferredWorkspace)) {
    return preferredWorkspace;
  }

  return workspaces[0] ?? null;
};

const getActiveWorkspace = (
  workspaces: WorkspaceType[],
  preferredActiveWorkspace?: WorkspaceType | null,
  defaultWorkspace?: WorkspaceType | null
): WorkspaceType | null => {
  if (preferredActiveWorkspace && workspaces.includes(preferredActiveWorkspace)) {
    return preferredActiveWorkspace;
  }

  if (defaultWorkspace && workspaces.includes(defaultWorkspace)) {
    return defaultWorkspace;
  }

  return workspaces[0] ?? null;
};

const getCapabilitiesFromRoles = (
  roles: UserRoleType[],
  ageGroup: AgeGroupType | null
): UserWorkspaceCapabilities => {
  const isTutorLike = roles.includes("teacher") || roles.includes("tutor") || roles.includes("admin");
  const isParent = roles.includes("parent");

  return {
    canAccessStudentWorkspace: roles.includes("student"),
    canAccessTutorWorkspace: isTutorLike,
    canAccessParentWorkspace: isParent,
    canAccessAdminWorkspace: false,
    canLearn: true,
    canPlay: true,
    canReview: true,
    canCreateQuiz: isTutorLike,
    canManageClassrooms: isTutorLike,
    canManageChildren: isParent,
    canViewQuizPlans: isTutorLike || (isParent && ageGroup === "16plus"),
    canManageSchoolQuizzes: roles.includes("admin"),
  };
};

const normalizeCapabilities = (
  capabilities: UserWorkspaceCapabilities | undefined,
  fallbackCapabilities: UserWorkspaceCapabilities
): UserWorkspaceCapabilities => {
  const source = capabilities ?? ({} as UserWorkspaceCapabilities);

  const getBool = (value: boolean | undefined, fallback: boolean) => {
    return typeof value === "boolean" ? value : fallback;
  };

  return {
    canAccessStudentWorkspace: getBool(source.canAccessStudentWorkspace, fallbackCapabilities.canAccessStudentWorkspace),
    canAccessTutorWorkspace: getBool(source.canAccessTutorWorkspace, fallbackCapabilities.canAccessTutorWorkspace),
    canAccessParentWorkspace: getBool(source.canAccessParentWorkspace, fallbackCapabilities.canAccessParentWorkspace),
    canAccessAdminWorkspace: getBool(source.canAccessAdminWorkspace, fallbackCapabilities.canAccessAdminWorkspace),
    canLearn: getBool(source.canLearn, fallbackCapabilities.canLearn),
    canPlay: getBool(source.canPlay, fallbackCapabilities.canPlay),
    canReview: getBool(source.canReview, fallbackCapabilities.canReview),
    canCreateQuiz: getBool(source.canCreateQuiz, fallbackCapabilities.canCreateQuiz),
    canManageClassrooms: getBool(source.canManageClassrooms, fallbackCapabilities.canManageClassrooms),
    canManageChildren: getBool(source.canManageChildren, fallbackCapabilities.canManageChildren),
    canViewQuizPlans: getBool(source.canViewQuizPlans, fallbackCapabilities.canViewQuizPlans),
    canManageSchoolQuizzes: getBool(source.canManageSchoolQuizzes, fallbackCapabilities.canManageSchoolQuizzes),
  };
};

const getPermissions = (
  roles: UserRoleType[],
  ageGroup: AgeGroupType | null,
  capabilities: UserWorkspaceCapabilities
): UserPermissionType[] => {
  const permissions = new Set<UserPermissionType>(["learn", "play", "review"]);

  if (roles.includes("admin")) {
    permissions.add("create_quiz");
    permissions.add("manage_classrooms");
    permissions.add("manage_school_quizzes");
    permissions.add("view_quiz_plans");
  }

  if (roles.includes("teacher") || roles.includes("tutor")) {
    permissions.add("create_quiz");
    permissions.add("manage_classrooms");
    if (ageGroup === "16plus") {
      permissions.add("view_quiz_plans");
    }
  }

  if (roles.includes("parent")) {
    permissions.add("manage_children");
    if (ageGroup === "16plus") {
      permissions.add("view_quiz_plans");
    }
  }

  if (capabilities.canAccessAdminWorkspace) {
    permissions.add("access_admin_workspace");
  }

  return Array.from(permissions);
};

export const buildUserInfo = ({
  roleId,
  accountTypeId,
  age,
  roles,
  profiles,
  isTester,
  activeRole,
  workspaces,
  activeWorkspace,
  defaultWorkspace,
  capabilities,
}: {
  roleId?: number;
  accountTypeId?: number;
  age?: number | null;
  roles?: UserRoleType[];
  profiles?: UserProfileType[];
  isTester?: boolean;
  activeRole?: ActiveRoleType;
  workspaces?: WorkspaceType[];
  activeWorkspace?: WorkspaceType | null;
  defaultWorkspace?: WorkspaceType | null;
  capabilities?: UserWorkspaceCapabilities;
}): UserInfoType => {
  const normalizedAge = typeof age === "number" ? age : null;
  const ageGroup = getAgeGroup(normalizedAge);
  const normalizedAccountTypeId = accountTypeId ?? 0;
  const normalizedRoles = normalizeRoles(roles);
  const normalizedProfiles = normalizeProfiles(profiles);
  const resolvedRoles =
    normalizedRoles.length > 0
      ? normalizedRoles
      : getRolesFromRoleId(roleId ?? 0, normalizedAccountTypeId);
  const resolvedWorkspaces = (() => {
    const normalizedWorkspaces = normalizeWorkspaces(workspaces);

    if (normalizedWorkspaces.length > 0) {
      return normalizedWorkspaces;
    }

    return getWorkspacesFromRoles(resolvedRoles);
  })();
  const resolvedDefaultWorkspace = getDefaultWorkspace(resolvedWorkspaces, defaultWorkspace);
  const resolvedCapabilities = normalizeCapabilities(
    capabilities,
    getCapabilitiesFromRoles(resolvedRoles, ageGroup)
  );

  return {
    roles: resolvedRoles,
    profiles: normalizedProfiles,
    isTester: isTester === true,
    activeRole: getActiveRole(resolvedRoles, activeRole),
    workspaces: resolvedWorkspaces,
    activeWorkspace: getActiveWorkspace(
      resolvedWorkspaces,
      activeWorkspace,
      resolvedDefaultWorkspace
    ),
    defaultWorkspace: resolvedDefaultWorkspace,
    capabilities: resolvedCapabilities,
    permissions: getPermissions(resolvedRoles, ageGroup, resolvedCapabilities),
    roleId: roleId ?? 0,
    accountTypeId: normalizedAccountTypeId,
    age: normalizedAge,
    ageGroup,
  };
};

export const canAccessStudentArea = (userInfo: UserInfoType): boolean => {
  return userInfo.permissions.includes("learn");
};

export const canAccessCreatorArea = (userInfo: UserInfoType): boolean => {
  return userInfo.permissions.includes("manage_classrooms");
};

export const canAccessParentArea = (userInfo: UserInfoType): boolean => {
  return userInfo.capabilities.canAccessParentWorkspace;
};

export const canAccessSchoolArea = (userInfo: UserInfoType): boolean => {
  return userInfo.permissions.includes("manage_school_quizzes");
};

export const canAccessWebPath = (pathname: string, userInfo: UserInfoType): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  if (["en", "pt", "es", "de", "fr", "it"].includes(segments[0])) segments.shift();
  const route = `/${segments.join("/")}`;
  if (route === "/tutors/centres/admin-workspace-access" || route === "/tutors/classroom/join" || route.startsWith("/tutors/classroom/join/")) return true;
  if (route === "/admin" || route.startsWith("/admin/") || route === "/tutors/centres" || route.startsWith("/tutors/centres/")) {
    return userInfo.capabilities.canAccessAdminWorkspace === true;
  }
  if (route === "/tutors" || route.startsWith("/tutors/")) return canAccessCreatorArea(userInfo);
  if (route === "/parents" || route.startsWith("/parents/")) return canAccessParentArea(userInfo);
  if (route === "/claimant" || route.startsWith("/claimant/")) return userInfo.roles.includes("claimant") && userInfo.workspaces.includes("claimant");
  return true;
};

export const canAccessAdminArea = (userInfo: UserInfoType): boolean => {
  return userInfo.permissions.includes("access_admin_workspace");
};